<template>
  <div
    ref="container"
    class="relative "
    :class="textClass"
    @click="expanded = !expanded"
  >
    <!-- Current selection display -->
    <div
      class="h-[53px] desk:border-b border-transparent hover:border-darkest
      transition-all duration-300 ease-out
      flex gap-8 items-center
      type-sm cursor-pointer"
      :class="{
        'border-border-strong': expanded,
        'border-transparent': !expanded,
      }"
    >
      <div class="w-20 h-16">
        <img
          :src="currentLanguage.flagUrl"
          :alt="currentLanguage.label"
          class="object-contain rounded-2"
        >
      </div>
      <span>{{ currentLanguage.label }}</span>
    </div>

    <!-- Dropdown menu -->
    <div
      v-if="expanded"
      class="absolute top-full mt-1 bg-lightest outline outline-1 outline-bg-inverse/10 shadow-lg z-modals min-w-[280px] max-h-[400px] overflow-y-auto py-6 -right-10 desk:right-0"
      :class="{
        'bottom-48 left-1/2 transform -translate-x-1/2 !top-auto': footerStyling
      }"
    >
      <a
        v-for="lang in languages"
        :key="lang.url"
        :href="lang.url"
        class="flex items-center gap-8 px-16 py-8 hover:bg-bg-hover transition-colors type-sm"
        :class="{
          'bg-bg-selected': lang.url === currentLanguage?.url
        }"
        @click.stop="navigateToLanguage(lang.url)"
      >
        <div class="rounded-2 overflow-hidden w-16 h-12 shrink-0">
          <img
            :src="lang.flagUrl"
            :alt="lang.label"
            class="w-full h-full object-contain"
          >
        </div>
        <span class="type-sm">{{ lang.label }}</span>
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';
import { useGlobalContentStore } from '~/store/globalContent';

const globalContent = useGlobalContentStore();
const { $t } = useNuxtApp();
const expanded = ref(false);
const container = useTemplateRef('container');

onClickOutside(container, () => {
  expanded.value = false;
});

const props = defineProps({
  invertTextColor: {
    type: Boolean,
    default: null,
  },
  footerStyling: Boolean,
});

const textClass = computed(() => {
  if (props.invertTextColor === null) {
    return '';
  }
  return props.invertTextColor ? 'text-text-inverse' : 'text-text-base';
});

type LanguageOption = {
  label: string;
  url: string;
  countryCode: string;
  flagUrl: string;
}

const languages = computed<LanguageOption[]>(() => {
  const marketKeys = ['corporate-en', 'corporate-se'];

  const flagMap: Record<string, string> = {
    'corporate-en': '/flags/GB-NIR.svg',
    'corporate-se': '/flags/SE.svg',
  };

  return marketKeys.map(key => ({
    key,
    label: $t(`lang.selector.label.${key}`),
    url: $t(`lang.selector.url.${key}`),
    countryCode: key.toUpperCase(),
    flagUrl: flagMap[key],
  }));
});

const currentLanguage = computed(() => {
  const currentCountryCode = globalContent.getCountryCode?.toUpperCase() || '';

  const found = languages.value.find(lang => lang.countryCode === currentCountryCode);

  return found ?? {
    label: 'fallback' + currentCountryCode,
    url: 'https://www.kahrs.com/en/',
    countryCode: currentCountryCode,
    flagUrl: '/flags/GB-NIR.svg',
  };
});

const navigateToLanguage = (url: string) => {
  window.location.href = url;
};
</script>
